import React from 'react';

export default function DomainServicesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Domain Services</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Domain Service ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Encapsule la logique métier qui n'appartient pas naturellement à une entité</li>
                                <li>Opère sur plusieurs entités ou agrégats</li>
                                <li>Ne maintient pas d'état (stateless)</li>
                                <li>Représente une opération ou un processus métier</li>
                                <li>Contient uniquement la logique pure du domaine</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure d'un Domain Service</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`public class PaymentValidationDomainService {
    // Pas de dépendances d'infrastructure
    // Pas d'état

    public ValidationResult validatePayment(
        Payment payment,
        PaymentLimits limits,
        BusinessCalendar calendar
    ) {
        List<ValidationError> errors = new ArrayList<>();
        
        // Logique de validation pure
        validateAmount(payment, limits, errors);
        validateDates(payment, calendar, errors);
        validateParties(payment, errors);
        
        return new ValidationResult(errors.isEmpty(), errors);
    }

    private void validateAmount(
        Payment payment, 
        PaymentLimits limits,
        List<ValidationError> errors
    ) {
        // Règles métier pures
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples Concrets</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">FeeCalculationDomainService</h4>
                                <pre className="text-xs mt-2">
{`public class FeeCalculationDomainService {
    public Amount calculateFees(
        Payment payment,
        FeeStructure feeStructure,
        CustomerCategory category
    ) {
        // Calcul des frais basé sur:
        // - Type de paiement
        // - Montant
        // - Catégorie client
        // - Structure tarifaire
        return feeStructure
            .forCategory(category)
            .calculate(payment);
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">PaymentRoutingDomainService</h4>
                                <pre className="text-xs mt-2">
{`public class PaymentRoutingDomainService {
    public RoutingDecision determineRoute(
        Payment payment,
        RoutingRules rules,
        NetworkStatus status
    ) {
        // Logique de routage basée sur:
        // - Caractéristiques du paiement
        // - Règles de routage
        // - État des réseaux
        return rules.apply(payment, status);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Mise en œuvre</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Service de Workflow</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`public class PaymentWorkflowDomainService {
    public WorkflowTransition evaluateTransition(
        Payment payment,
        WorkflowState currentState,
        WorkflowEvent event
    ) {
        // Logique de transition pure
        if (event == WorkflowEvent.VALIDATE) {
            return evaluateValidationTransition(
                payment, 
                currentState
            );
        }
        // ...
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Service de Règles Métier</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`public class BusinessRulesDomainService {
    public RuleEvaluation evaluateRules(
        Payment payment,
        RuleSet rules
    ) {
        List<RuleResult> results = new ArrayList<>();
        
        for (BusinessRule rule : rules) {
            results.add(rule.evaluate(payment));
        }
        
        return new RuleEvaluation(results);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Caractéristiques Clés</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Logique Métier Pure</h4>
                            <p className="text-sm text-gray-600">
                                Contient uniquement des règles métier sans dépendance technique
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Sans État</h4>
                            <p className="text-sm text-gray-600">
                                Ne maintient aucun état entre les appels, toujours déterministe
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Opérations Multi-Entités</h4>
                            <p className="text-sm text-gray-600">
                                Coordonne les opérations impliquant plusieurs entités ou agrégats
                            </p>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Réutilisable</h4>
                            <p className="text-sm text-gray-600">
                                Implémente la logique métier réutilisable dans différents contextes
                            </p>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Testable</h4>
                            <p className="text-sm text-gray-600">
                                Facilement testable car sans dépendance externe
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}